import CloseIcon from '@mui/icons-material/Close';
import {
  alpha,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { USER_LOAN_ESTIMATION_TYPE } from '@/config/constants';
import { LOAN_PATH } from '@/config/loanNavPaths';
import { useUpdateUserMutation } from '@/apis/extended/userApi';

interface InvestorOnboardingModalProps {
  open: boolean;
  onClose: () => void;
}

/** "I'm an investor" — offered instead of the income qualification flow for commercial/investor leads. */
export const InvestorOnboardingModal = ({
  open,
  onClose,
}: InvestorOnboardingModalProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [updateUser, { isLoading }] = useUpdateUserMutation();

  const choose = async (path: 'income' | 'pfs') => {
    try {
      if (path === 'income') {
        await updateUser({
          onboardingCompleted: true,
          onboardingIntent: 'investment',
        }).unwrap();
        onClose();
        navigate(LOAN_PATH.qualifications);
        return;
      }
      await updateUser({
        onboardingCompleted: true,
        onboardingIntent: 'investment',
        loanEstimationType: USER_LOAN_ESTIMATION_TYPE.COMMERCIAL,
      }).unwrap();
      onClose();
      navigate(LOAN_PATH.personalFinancialStatement);
    } catch {
      toast.error('Could not save your preference. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: theme.palette.mainColors.background,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          I&apos;m an investor
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Choose how you&apos;d like to qualify. Both lead to loan estimates —
          pick whichever fits your deal.
        </Typography>

        <Card variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
          <CardActionArea
            disabled={isLoading}
            onClick={() => void choose('income')}
            sx={{ p: 2 }}
          >
            <CardContent sx={{ p: 0 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Income Qualification
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Standard income, credit, and debt inputs — same as our purchase
                and DSCR flows.
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardActionArea
            disabled={isLoading}
            onClick={() => void choose('pfs')}
            sx={{ p: 2 }}
          >
            <CardContent sx={{ p: 0 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Personal Financial Statement
              </Typography>
              <Typography variant="body2" color="text.secondary">
                For commercial real estate: a net-worth and asset/liability
                statement plus your schedule of real estate owned — the standard
                document commercial lenders qualify you on.
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Not sure? Income Qualification is faster for a single rental
            property; Personal Financial Statement is the better fit for larger
            commercial deals or portfolios.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
